// Canned support notes for the Add Note dialog.
//
// Cancellation policy (CP): cancel more than 48 hours out and the rider gets a Hitch
// credit; inside 48 hours the trip is non-refundable. Since January 2026 every user gets
// ONE one-time exception on a late cancellation, refunded either as Hitch credit or as
// cash back to their card. Anything past the first exception has to be approved by
// Noelle. Support records each exception as a note on the user so the next agent can
// see it has been used. The standard note is "CP One Time Excep 48HRS" followed by the
// refund method, the agent's initials and the date, e.g.
// "CP One Time Excep 48HRS Cash ND 11/03/2026 - First exception".
//
// Shared-ride cancellation test: half of shared-ride riders book under a nonrefundable
// fare (shown on the confirmation page before booking); the other half keep the 48-hour
// policy. Exception notes record which policy the rider was on.
import moment from 'moment';

export const CUSTOM_TEMPLATE_ID = 'custom';
export const EXCEPTION_TEMPLATE_ID = 'cp_exception';

// The one-exception-per-user policy started 1/1/2026. Exception notes from 2025 or
// earlier don't count against the user.
export const EXCEPTION_POLICY_START = moment('2026-01-01');

// Support agents' initials as they sign notes. Add new team members here.
export const SUPPORT_TEAM_INITIALS = ['ND', 'KO', 'VA', 'CM', 'NO', 'HE', 'DL', 'FR'];

export const NOTE_TEMPLATES = [
  {
    id: EXCEPTION_TEMPLATE_ID,
    label: 'CP exception',
    description: 'One-time exception on a late (<48h) cancellation',
    fields: ['policy', 'refundMethod', 'approval', 'date', 'initials'],
  },
];

// Every field a template lists is required; the dialog has no defaults.
export const FIELD_LABELS = {
  policy: 'Cancellation policy',
  refundMethod: 'Refund to',
  approval: 'Exception',
  date: 'Date',
  initials: 'Initials',
};

// Which cancellation policy the rider booked under (check the trip in Admin).
export const CANCELLATION_POLICIES = [
  { id: '48hr', label: '48-hour policy', text: '48HRS' },
  { id: 'nonrefundable_test', label: 'Nonrefundable test', text: 'NONREFUNDABLE TEST' },
];

export const REFUND_METHODS = [
  { id: 'cash', label: 'Cash — back to card', text: 'Cash' },
  { id: 'credit', label: 'Hitch credits', text: 'Credit' },
];

export const EXCEPTION_APPROVALS = [
  { id: 'first', label: 'First exception', text: 'First exception' },
  { id: 'noelle', label: 'Second exception approved by Noelle', text: 'Second exception approved by Noelle' },
];

export const findTemplate = (id) => NOTE_TEMPLATES.find((t) => t.id === id) || null;

// The required fields of a template that haven't been picked yet, as {name, label}.
export function missingTemplateFields(templateId, values = {}) {
  const template = findTemplate(templateId);
  if (!template) {
    return [];
  }
  return template.fields.filter((name) => !values[name]).map((name) => ({ name, label: FIELD_LABELS[name] }));
}

const findById = (list, id) => list.find((item) => item.id === id) || null;

export const formatNoteDate = (date = new Date()) => moment(date).format('M/DD/YYYY');

export const normalizeInitials = (initials) =>
  (initials || '')
    .replace(/[^a-z]/gi, '')
    .toUpperCase()
    .slice(0, 4);

// "CP One Time Excep 48HRS Cash ND 11/03/2026 - First exception"
// "CP One Time Excep NONREFUNDABLE TEST Credit ND 11/03/2026 - Second exception approved by Noelle"
// Initials are left out when blank.
export function buildTemplateNote(templateId, { policy, refundMethod, approval, initials, date } = {}) {
  const signature = [normalizeInitials(initials), formatNoteDate(date || undefined)].filter(Boolean).join(' ');

  if (templateId === EXCEPTION_TEMPLATE_ID) {
    const method = findById(REFUND_METHODS, refundMethod) || REFUND_METHODS[0];
    const approvalText = (findById(EXCEPTION_APPROVALS, approval) || EXCEPTION_APPROVALS[0]).text;
    const policyText = (findById(CANCELLATION_POLICIES, policy) || CANCELLATION_POLICIES[0]).text;
    return `CP One Time Excep ${policyText} ${method.text} ${signature} - ${approvalText}`;
  }

  return '';
}

// Matches hand-typed variants ("One Time Excep", "one-time exception", "1x excep") but
// not a hand-typed denial ("one time excep denied").
const EXCEPTION_PATTERN = /\b(one[\s-]*time|1x)[\s-]*excep/i;
const DENIED_PATTERN = /\bdenied\b/i;

// Exception notes that count against the user: written on or after the policy start.
// A note with no readable date is kept, so the agent still sees it.
export function findPriorExceptionNotes(notes) {
  if (!Array.isArray(notes)) {
    return [];
  }
  return notes.filter((note) => {
    const body = note?.body || '';
    if (!EXCEPTION_PATTERN.test(body) || DENIED_PATTERN.test(body)) {
      return false;
    }
    const createdAt = moment(note.createdAt);
    return !note.createdAt || !createdAt.isValid() || !createdAt.isBefore(EXCEPTION_POLICY_START);
  });
}
